import { useEffect, useState, useContext } from 'react';
import $ from "jquery";
import { Accordion, Button, ListGroup, Card, Form, Alert, ButtonGroup } from 'react-bootstrap';
import { BsPeople, BsInboxesFill, BsHouse, BsHouseDoorFill, BsHouseFill } from 'react-icons/bs';
import Spinner from 'react-bootstrap/Spinner';
import {
    useNavigate
} from "react-router-dom";
import UserContext from '../../contexts/UserContext';
import ExcelJS from 'exceljs';

const Attendance = () => {
    const navigate = useNavigate();
    const [rsvpData, setRsvpData] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);
    const [dateToShow, setDateToShow] = useState(null);
    const [selectedMealType, setSelectedMealType] = useState('lunch');
    const [menuItems, setMenuItems] = useState([]);

    function getRSVPData(mealType = 'lunch') {
        setLoading(true);
        $.ajax({
            type: "GET",
            url: process.env.REACT_APP_API_URL + `/getRSVPData?mealType=${mealType}`,
            xhrFields: {
                withCredentials: true  // Include cookies
            },
            crossDomain: true,
            success: function (response) {
                console.log(response)
                if (response.message && response.message.includes("Error in auth")) {
                    navigate("/login", { state: { message: response.message } })
                }
                
                setRsvpData(response.data?.records || []);
                setDateToShow(response.data?.date);
                setMenuItems(response.data?.menuItems || []);
                setLoading(false);
            },
            error: function (err) {
                console.log("error")
                console.log(err)
                setLoading(false);
                setRsvpData([]);
                setMessage("Error fetching data");
            }
        })
    }

async function processAndDownloadExcel(data, mealType, menuItems) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(`${mealType.charAt(0).toUpperCase() + mealType.slice(1)} Data`);
    
    // Simplified headers - added thaaliNo, removed mohalla/submohalla
    const headers = ["Thaali No", "HOF", "Name", `${mealType.charAt(0).toUpperCase() + mealType.slice(1)} Requirement`];
    const headerRow = worksheet.addRow(headers);
    
    // Style header row
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF884A39' }
    };
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
    
    // Sort data by thaaliNo in ascending order
    // Handle cases where thaaliNo might be missing, null, or non-numeric
    const sortedData = [...data].sort((a, b) => {
        const thaaliA = a.thaaliNo;
        const thaaliB = b.thaaliNo;
        
        // Handle missing or null values - put them at the end
        if (!thaaliA && !thaaliB) return 0;
        if (!thaaliA) return 1;
        if (!thaaliB) return -1;
        
        // Try to parse as numbers for proper numeric sorting
        const numA = parseInt(thaaliA);
        const numB = parseInt(thaaliB);
        
        // If both are valid numbers, sort numerically
        if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
        }
        
        // If one is a number and other isn't, number comes first
        if (!isNaN(numA) && isNaN(numB)) return -1;
        if (isNaN(numA) && !isNaN(numB)) return 1;
        
        // If both are non-numeric, sort alphabetically
        return String(thaaliA).localeCompare(String(thaaliB));
    });
    
    // Add data rows
    sortedData.forEach((profile) => {
        const rowData = [
            profile.thaaliNo || "N/A",
            profile.sabeel,
            profile.name
        ];
        
        // Determine the combined meal requirement
        let combinedRequirement = "Not Selected";
        let combinedQuantity = null;
        
        if (profile.mealData && profile.mealData.length > 0) {
            // Get all quantities for this meal
            const quantities = profile.mealData.map(meal => meal.quantity);
            
            // Handle edge case where items might have different quantities
            const uniqueQuantities = [...new Set(quantities)];
            
            if (uniqueQuantities.length === 1) {
                // All items have the same quantity (normal case)
                combinedQuantity = uniqueQuantities[0];
                combinedRequirement = getFractionValue(combinedQuantity);
            } else {
                // Edge case: items have different quantities
                // Use the most common quantity, or the highest if tied
                const quantityCount = {};
                quantities.forEach(q => {
                    quantityCount[q] = (quantityCount[q] || 0) + 1;
                });
                
                // Find the most frequent quantity
                let maxCount = 0;
                let mostCommonQuantity = 0;
                
                Object.entries(quantityCount).forEach(([qty, count]) => {
                    const numQty = parseFloat(qty);
                    if (count > maxCount || (count === maxCount && numQty > mostCommonQuantity)) {
                        maxCount = count;
                        mostCommonQuantity = numQty;
                    }
                });
                
                combinedQuantity = mostCommonQuantity;
                combinedRequirement = `${getFractionValue(mostCommonQuantity)} (Mixed: ${quantities.map(q => getFractionValue(q)).join(', ')})`;
            }
        }
        
        // Add the combined requirement to row data
        rowData.push(combinedRequirement);
        
        const excelRow = worksheet.addRow(rowData);
        
        // Apply background color based on the primary quantity (column 4)
        const requirementCell = excelRow.getCell(4);
        
        if (combinedQuantity !== null) {
            switch (combinedQuantity) {
                case 0:
                    requirementCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFFFCCCC' } // Light red
                    };
                    break;
                case 0.5:
                    requirementCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFFFE6CC' } // Light orange
                    };
                    break;
                case 1:
                    requirementCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFCCFFCC' } // Light green
                    };
                    break;
                default:
                    // No background color for other values or mixed quantities
                    break;
            }
        }
        
        // Add borders to all cells in the row
        excelRow.eachCell((cell) => {
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
        
        // Left align text columns
        excelRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' }; // Thaali No
        excelRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' }; // HOF
        excelRow.getCell(3).alignment = { horizontal: 'left', vertical: 'middle' }; // Name
    });
    
    // Auto-fit columns
    worksheet.columns.forEach((column, index) => {
        let maxLength = headers[index]?.length || 10;
        
        // Check data length for better column width
        sortedData.forEach(profile => {
            let cellValue;
            switch (index) {
                case 0: cellValue = profile.thaaliNo || "N/A"; break;
                case 1: cellValue = profile.sabeel; break;
                case 2: cellValue = profile.name; break;
                case 3: 
                    // Calculate combined requirement length
                    if (profile.mealData && profile.mealData.length > 0) {
                        const quantities = profile.mealData.map(meal => meal.quantity);
                        const uniqueQuantities = [...new Set(quantities)];
                        if (uniqueQuantities.length === 1) {
                            cellValue = getFractionValue(uniqueQuantities[0]);
                        } else {
                            cellValue = `Mixed Requirements`;
                        }
                    } else {
                        cellValue = "Not Selected";
                    }
                    break;
                default: cellValue = ""; break;
            }
            if (cellValue && cellValue.toString().length > maxLength) {
                maxLength = cellValue.toString().length;
            }
        });
        
        column.width = Math.min(Math.max(maxLength + 2, 12), 40); // Min 12, Max 40
    });
    
    // Generate and download file
    try {
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${mealType}_combined_${dateToShow?.replace(/\s+/g, '_')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error generating Excel file:', error);
        setMessage('Error generating Excel file');
    }
}

    

    const handleDownload = () => {
        console.log("rsvpData for download:", rsvpData)
        if (rsvpData && rsvpData.length > 0) {
            processAndDownloadExcel(rsvpData, selectedMealType, menuItems);
        }
    };

    const handleMealTypeChange = (mealType) => {
        setSelectedMealType(mealType);
        getRSVPData(mealType);
    };

    useEffect(() => {
        getRSVPData(selectedMealType);
    }, []);

    function getFractionValue(value) {
        switch (value) {
            case 0: return "Not needed";
            case 0.25: return "1/2 Serving";
            case 0.5: return "1 Serving";
            case 0.75: return "1 1/2 Serving";
            case 1: return "2 Serving";
            default: return value ? value.toString() : "Not Selected";
        }
    }

    const UserCard = ({ profile, index }) => {
        return (
            <Card className="mb-3" style={{ borderLeft: '4px solid #884A39' }}>
                <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#FFF8F0' }}>
                    <div className="d-flex align-items-center">
                        <BsHouseFill className="mx-2" style={{ color: '#884A39' }} />
                        <strong>{profile.name}</strong>
                    </div>
                    <div className="text-muted">
                        <small>HOF: {profile.sabeel}</small>
                    </div>
                </Card.Header>
                <Card.Body>
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <small><strong>Mohalla:</strong> {profile.sector?.name || "N/A"}</small>
                        </div>
                        <div className="col-sm-6">
                            <small><strong>Sub Mohalla:</strong> {profile.subSector?.name || "N/A"}</small>
                        </div>
                    </div>
                    
                    <div className="mt-3">
                        <h6 style={{ color: '#884A39' }}>{selectedMealType.charAt(0).toUpperCase() + selectedMealType.slice(1)} Requirements:</h6>
                        <div className="row">
                            {profile.mealData.map((meal, idx) => (
                                <div key={idx} className="col-sm-6 mb-2">
                                    <div className="d-flex justify-content-between align-items-center p-2" 
                                         style={{ backgroundColor: '#F8F9FA', borderRadius: '4px' }}>
                                        <span><strong>{meal.item}:</strong></span>
                                        <span className={`badge ${meal.quantity > 0 ? 'bg-success' : 'bg-secondary'}`}>
                                            {getFractionValue(meal.quantity)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    const DataList = () => {
        return (
            <div>
                {rsvpData && rsvpData.length > 0 ? (
                    <div>
                        <Card className="text-center p-4" style={{ backgroundColor: '#FFC26F', borderRadius: '10px' }}>
                            <h6>{rsvpData.length} users have filled the filling request</h6>
                        </Card>
                        {/* {rsvpData.map((profile, index) => (
                            <UserCard key={profile.userId || index} profile={profile} index={index} />
                        ))} */}
                        <div className="text-center mt-4">
                            <Button 
                                className="px-4 py-2" 
                                style={{ backgroundColor: '#884A39', border: 'none' }} 
                                onClick={handleDownload}
                            >
                                Download {selectedMealType.charAt(0).toUpperCase() + selectedMealType.slice(1)} Report
                            </Button>
                        </div>
                    </div>
                ) : (
                    <Card className="text-center p-4" style={{ backgroundColor: '#FFC26F', borderRadius: '10px' }}>
                        <h6>No {selectedMealType} requirements found for {dateToShow}!</h6>
                    </Card>
                )}
            </div>
        );
    };

    const MealTypeSelector = () => {
        return (
            <div className="text-center mb-4">
                <h6 className="mb-3">Select Meal Type for {dateToShow}</h6>
                <ButtonGroup>
                    <Button
                        variant={selectedMealType === 'lunch' ? 'primary' : 'outline-primary'}
                        style={{
                            backgroundColor: selectedMealType === 'lunch' ? '#884A39' : 'transparent',
                            borderColor: '#884A39',
                            color: selectedMealType === 'lunch' ? 'white' : '#884A39'
                        }}
                        onClick={() => handleMealTypeChange('lunch')}
                    >
                        Lunch
                    </Button>
                    <Button
                        variant={selectedMealType === 'dinner' ? 'primary' : 'outline-primary'}
                        style={{
                            backgroundColor: selectedMealType === 'dinner' ? '#884A39' : 'transparent',
                            borderColor: '#884A39',
                            color: selectedMealType === 'dinner' ? 'white' : '#884A39'
                        }}
                        onClick={() => handleMealTypeChange('dinner')}
                    >
                        Dinner
                    </Button>
                </ButtonGroup>
                
                {menuItems.length > 0 && (
                    <div className="mt-3">
                        <small className="text-muted">
                            <strong>Available items:</strong> {menuItems.join(', ')}
                        </small>
                    </div>
                )}
            </div>
        );
    };

    const design = (
        <>
            {message ? <Alert variant='danger'>{message}!</Alert> : null}
            
            <div className="container-fluid">
                <div className="row justify-content-center">
                    <div className="col-lg-8">
                        <h4 className="text-center mb-4" style={{ color: '#884A39' }}>
                            RSVP Requirements Report
                        </h4>
                        
                        <MealTypeSelector />
                        <DataList />
                    </div>
                </div>
            </div>
        </>
    );

    return isLoading ? (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '200px' }}>
            <Spinner style={{ color: '#884A39' }} animation="border" role="status" />
            <span className="mt-2">Loading...</span>
        </div>
    ) : design;
}

export default Attendance;